import paper from 'paper';
import { PaperOffset } from 'paperjs-offset';
import {LEAF_TAGS, SKIP_ANCESTOR_TAGS, PRESENTATION_ATTRS, UNSUPPORTED_ATTRS} from '@/constants/editor';


function num(el, name, def = 0) {
  const v = el.getAttribute(name);
  return v == null || v === '' ? def : parseFloat(v);
}

function primitiveToPath(el) {
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case 'path': {
      const d = el.getAttribute('d');
      if (!d) return null;
      const path = new paper.CompoundPath(d);
      path.remove();
      return path;
    }

    case 'polygon':
    case 'polyline': {
      const pointsAttr = el.getAttribute('points') || '';
      const coords = pointsAttr
        .trim()
        .split(/[\s,]+/)
        .map(Number)
        .filter((n) => !Number.isNaN(n));

      if (coords.length < 4) return null;

      const segments = [];
      for (let i = 0; i + 1 < coords.length; i += 2) {
        segments.push([coords[i], coords[i + 1]]);
      }

      const path = new paper.Path({segments, insert: false});
      if (tag === 'polygon') path.closed = true;
      return path;
    }

    case 'rect': {
      const x = num(el, 'x');
      const y = num(el, 'y');
      const width = num(el, 'width');
      const height = num(el, 'height');
      if (width <= 0 || height <= 0) return null;

      const hasRx = el.hasAttribute('rx');
      const hasRy = el.hasAttribute('ry');
      const rx = hasRx ? num(el, 'rx') : hasRy ? num(el, 'ry') : 0;
      const ry = hasRy ? num(el, 'ry') : rx;

      if (rx > 0 || ry > 0) {
        return new paper.Path.Rectangle({
          point: [x, y], size: [width, height], radius: [rx, ry], insert: false,
        });
      }

      return new paper.Path.Rectangle({
        point: [x, y], size: [width, height], insert: false,
      });
    }

    case 'circle': {
      const cx = num(el, 'cx');
      const cy = num(el, 'cy');
      const r = num(el, 'r');
      if (r <= 0) return null;

      return new paper.Path.Circle({center: [cx, cy], radius: r, insert: false});
    }

    case 'ellipse': {
      const cx = num(el, 'cx');
      const cy = num(el, 'cy');
      const rx = num(el, 'rx');
      const ry = num(el, 'ry');
      if (rx <= 0 || ry <= 0) return null;

      return new paper.Path.Ellipse({
        center: [cx, cy], radius: [rx, ry], insert: false,
      });
    }

    case 'line': {
      const x1 = num(el, 'x1');
      const y1 = num(el, 'y1');
      const x2 = num(el, 'x2');
      const y2 = num(el, 'y2');

      return new paper.Path.Line({from: [x1, y1], to: [x2, y2], insert: false});
    }

    default:
      return null;
  }
}

function resolveUseElements(svgRoot) {
  const SVG_NS = process.env.REACT_APP_SVG_NS;
  const XLINK_NS = process.env.REACT_APP_XLINK_NS;

  let iterations = 0;
  const maxIterations = 20;

  while (iterations < maxIterations) {
    const useEl = svgRoot.querySelector('use');
    if (!useEl) break;

    iterations += 1;

    const href =
      useEl.getAttribute('href') ||
      useEl.getAttributeNS(XLINK_NS, 'href') ||
      useEl.getAttribute('xlink:href');

    if (!href || !href.startsWith('#')) {
      useEl.remove();
      continue;
    }

    const targetId = href.slice(1);
    let target = null;
    try {
      target = svgRoot.querySelector(`#${CSS.escape(targetId)}`);
    } catch (error) {
      target = svgRoot.querySelector(`[id="${targetId}"]`);
    }

    if (!target) {
      console.warn(`resolveUseElements: не знайдено елемент з id="${targetId}" для <use>`);
      useEl.remove();
      continue;
    }

    const clone = target.cloneNode(true);
    clone.removeAttribute('id');

    const x = num(useEl, 'x');
    const y = num(useEl, 'y');
    const useTransform = useEl.getAttribute('transform') || '';
    const translatePart = x || y ? `translate(${x} ${y})` : '';
    const combinedTransform = `${useTransform} ${translatePart}`.trim();

    const wrapperG = document.createElementNS(SVG_NS, 'g');
    if (combinedTransform) wrapperG.setAttribute('transform', combinedTransform);

    PRESENTATION_ATTRS.forEach((attr) => {
      const v = useEl.getAttribute(attr);
      if (v != null) wrapperG.setAttribute(attr, v);
    });

    wrapperG.appendChild(clone);
    useEl.parentNode.replaceChild(wrapperG, useEl);
  }

  if (iterations >= maxIterations) {
    console.warn('resolveUseElements: досягнуто ліміту ітерацій — можлива циклічна <use> посилання');
  }
}

function logTagDiagnostics(svgRoot, container) {
  const counts = new Map();

  Array.from(svgRoot.querySelectorAll('*')).forEach((el) => {
    const tag = el.tagName.toLowerCase();
    counts.set(tag, (counts.get(tag) || 0) + 1);
  });

  const summary = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([tag, count]) => `${tag}: ${count}`)
    .join(', ');

  const unsupported = UNSUPPORTED_ATTRS.filter((tag) =>
    counts.has(tag)
  );

  if (unsupported.length) {
    console.warn(
      `[extractSvgLeavesViaDOM] знайдено НЕпідтримувані теги: ${unsupported.join(', ')} — ` +
      'вони не конвертуються в paper.Path і будуть проігноровані.'
    );
  }
}

function isInsideSkipped(el, container) {
  let p = el.parentElement;
  while (p && p !== container) {
    if (SKIP_ANCESTOR_TAGS.has(p.tagName.toLowerCase())) return true;
    p = p.parentElement;
  }
  return false;
}

function cssColorWithAlpha(cssColor, alphaMultiplier) {
  if (!cssColor || cssColor === 'none') return null;

  try {
    const color = new paper.Color(cssColor);
    if (alphaMultiplier != null && !Number.isNaN(alphaMultiplier)) {
      color.alpha = (color.alpha ?? 1) * alphaMultiplier;
    }
    return color;
  } catch (error) {
    console.warn('cssColorWithAlpha: не вдалось розпарсити колір', cssColor, error);
    return null;
  }
}

function uniformScaleFromCtm(ctm) {
  const det = ctm.a * ctm.d - ctm.b * ctm.c;
  return Math.sqrt(Math.abs(det)) || 1;
}
function bakeStrokeIntoGeometry(path) {
  const hasRealFill = !!(path.fillColor && path.fillColor.alpha > 0);
  const hasRealStroke = !!(path.strokeColor && path.strokeWidth > 0);
  const hasDashArray = Array.isArray(path.dashArray) && path.dashArray.length > 0;

  if (!hasRealStroke || hasDashArray) return [path];

  try {
    const outline = PaperOffset.offsetStroke(path, path.strokeWidth / 2, {
      join: path.strokeJoin || 'miter',
      cap: path.strokeCap || 'butt',
    });

    if (!outline) return [path];

    outline.fillColor = path.strokeColor;
    outline.fillRule = path.fillRule || 'nonzero';
    outline.strokeColor = null;
    outline.strokeWidth = 0;
    outline.opacity = path.opacity;
    if (path.name) outline.name = `${path.name || 'shape'}_stroke`;
    outline.remove();

    if (!hasRealFill) {
      path.remove();
      return [outline];
    }

    const sameColor =
      path.fillColor && outline.fillColor &&
      path.fillColor.toCSS(true) === outline.fillColor.toCSS(true);

    if (sameColor) {
      const filled = path.clone({insert: false});
      filled.strokeColor = null;
      const merged = filled.unite(outline, {insert: false});
      merged.fillColor = path.fillColor;
      merged.fillRule = path.fillRule || 'nonzero';
      merged.opacity = path.opacity;
      if (path.name) merged.name = path.name;
      merged.remove();

      filled.remove();
      outline.remove();
      path.remove();
      return [merged];
    }
    const filled = path.clone({insert: false});
    filled.strokeColor = null;
    if (path.name) filled.name = `${path.name || 'shape'}_fill`;

    path.remove();
    return [filled, outline];
  } catch (error) {
    console.warn('bakeStrokeIntoGeometry: offsetStroke не вдався', error);
    return [path];
  }
}

export function extractSvgLeavesViaDOM(svgString) {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-99999px';
  container.style.top = '-99999px';
  container.style.width = '0';
  container.style.height = '0';
  container.style.overflow = 'hidden';

  document.body.appendChild(container);

  try {
    container.innerHTML = svgString;

    const svgRoot = container.querySelector('svg');
    if (!svgRoot) {
      throw new Error('Не знайдено кореневий <svg> елемент');
    }

    resolveUseElements(svgRoot);
    logTagDiagnostics(svgRoot, container);

    const allElements = Array.from(svgRoot.querySelectorAll('*'));
    const items = [];

    allElements.forEach((el) => {
      const tag = el.tagName.toLowerCase();

      if (!LEAF_TAGS.has(tag)) return;
      if (isInsideSkipped(el, container)) return;

      let style;
      try {
        style = window.getComputedStyle(el);
      } catch (error) {
        console.warn('extractSvgLeavesViaDOM: getComputedStyle не вдався', error);
        return;
      }

      if (style.display === 'none' || style.visibility === 'hidden') return;

      let path;
      try {
        path = primitiveToPath(el);
      } catch (error) {
        console.warn('extractSvgLeavesViaDOM: не вдалось конвертувати елемент', tag, error);
        return;
      }

      if (!path) return;

      let ctm = null;
      try {
        ctm = typeof el.getCTM === 'function' ? el.getCTM() : null;
      } catch (error) {
        ctm = null;
      }

      let scaleForStroke = 1;

      if (ctm) {
        const matrix = new paper.Matrix(ctm.a, ctm.b, ctm.c, ctm.d, ctm.e, ctm.f);
        path.transform(matrix);
        scaleForStroke = uniformScaleFromCtm(ctm);
      }

      const fillOpacity = parseFloat(style.fillOpacity || '1');
      const strokeOpacity = parseFloat(style.strokeOpacity || '1');
      const opacity = parseFloat(style.opacity || '1');

      path.fillColor = cssColorWithAlpha(style.fill, fillOpacity);
      path.strokeColor = cssColorWithAlpha(style.stroke, strokeOpacity);

      const rawStrokeWidth = parseFloat(style.strokeWidth || '0');
      if (path.strokeColor && rawStrokeWidth > 0) {
        path.strokeWidth = rawStrokeWidth * scaleForStroke;
      }

      if (path.strokeColor) {
        const linejoin = style.strokeLinejoin;
        const linecap = style.strokeLinecap;
        const miterLimit = parseFloat(style.strokeMiterlimit || '4');

        if (linejoin) path.strokeJoin = linejoin;
        if (linecap) path.strokeCap = linecap;
        if (!Number.isNaN(miterLimit)) path.miterLimit = miterLimit;

        const dashArrayAttr = style.strokeDasharray;
        if (dashArrayAttr && dashArrayAttr !== 'none') {
          path.dashArray = dashArrayAttr
            .split(/[\s,]+/)
            .map((n) => parseFloat(n) * scaleForStroke)
            .filter((n) => !Number.isNaN(n));
        }
      }

      if (typeof path.fillRule !== 'undefined') {
        const fillRule = style.fillRule || style.clipRule;
        if (fillRule === 'evenodd' || fillRule === 'nonzero') {
          path.fillRule = fillRule;
        }
      }
      path.opacity = Number.isNaN(opacity) ? 1 : opacity;
      if (el.id) path.name = el.id;
      const bakedPaths = bakeStrokeIntoGeometry(path);
      bakedPaths.forEach((p) => items.push(p));
    });

    return {items, svgRoot};
  } finally {
    container.remove();
  }
}